import { CheckIcon, TicketPercentIcon } from 'lucide-react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/shared/components/ui/drawer';
import { cn } from '@/shared/lib/utils';

import type { StoreCoupon } from '../types/store-coupon';
import { couponConditions, couponShortLabel } from '../utils/store-benefits';

interface StoreCouponsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  coupons: StoreCoupon[];
  /** Código já escolhido nesta sessão (vai ser aplicado no carrinho). */
  selectedCode: string | null;
  onSelect: (coupon: StoreCoupon) => void;
}

/**
 * Lista os cupons da loja (PRAT-110). "Usar" guarda o cupom como pendente da
 * sessão — o mesmo caminho do link `?cupom=` — e o checkout aplica sozinho.
 */
export function StoreCouponsDrawer({
  isOpen,
  onClose,
  coupons,
  selectedCode,
  onSelect,
}: StoreCouponsDrawerProps) {
  return (
    <Drawer open={isOpen} onOpenChange={open => !open && onClose()} shouldScaleBackground={false}>
      <DrawerContent className="flex max-h-[85vh] w-full flex-col">
        <div className="px-5 pt-4 pb-2">
          <DrawerTitle className="text-lg font-bold text-night">Cupons da loja</DrawerTitle>
          <DrawerDescription className="text-sm text-gray-500">
            Escolha um cupom: ele entra sozinho no seu pedido.
          </DrawerDescription>
        </div>
        <ul className="flex flex-col gap-3 overflow-y-auto px-5 pt-2 pb-8">
          {coupons.map(coupon => {
            const isSelected = selectedCode?.toUpperCase() === coupon.code.toUpperCase();
            const conditions = couponConditions(coupon);
            // PRAT-204: o cupom aparece, mas só pode ser escolhido no dia/horário dele.
            const isOutOfWindow = coupon.available_now === false;
            return (
              <li
                key={coupon.id}
                className={cn(
                  'relative flex items-stretch overflow-hidden rounded-2xl border bg-white',
                  isSelected ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-gray-200'
                )}
              >
                <div className="flex w-20 shrink-0 flex-col items-center justify-center gap-1 bg-emerald-50 px-2 text-emerald-700">
                  <TicketPercentIcon className="size-5" aria-hidden />
                  <span className="text-center text-xs font-bold leading-tight">
                    {couponShortLabel(coupon)}
                  </span>
                </div>
                {/* Picote do cupom */}
                <div aria-hidden className="w-0 border-l-2 border-dashed border-gray-200" />
                <div className="flex flex-1 flex-col gap-1 px-3 py-3">
                  <p className="text-sm font-semibold text-night line-clamp-1">
                    {coupon.name || couponShortLabel(coupon)}
                  </p>
                  {coupon.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">{coupon.description}</p>
                  )}
                  {conditions.length > 0 && (
                    <p className="text-[11px] text-gray-500">{conditions.join(' · ')}</p>
                  )}
                  <p className="text-[11px] font-mono font-semibold tracking-wide text-gray-400">
                    {coupon.code}
                  </p>
                </div>
                <div className="flex items-center pr-3">
                  <button
                    type="button"
                    onClick={() => onSelect(coupon)}
                    disabled={isSelected || isOutOfWindow}
                    className={cn(
                      'flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-bold clickable-button',
                      isSelected
                        ? 'bg-emerald-50 text-emerald-700'
                        : isOutOfWindow
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    )}
                  >
                    {isSelected ? (
                      <>
                        <CheckIcon className="size-3.5" aria-hidden /> Escolhido
                      </>
                    ) : isOutOfWindow ? (
                      'Indisponível'
                    ) : (
                      'Usar'
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </DrawerContent>
    </Drawer>
  );
}
